import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet/hooks";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

interface MarkerData {
  id: string;
  coords: [number, number];
}

interface MarkerClusterGroupProps {
  markers: MarkerData[];
  onMarkerClick: (id: string) => void;
  selectedId?: string | null;
}

function buildMemberIcon(selected: boolean): L.DivIcon {
  const ring = selected
    ? `box-shadow:0 0 0 4px hsl(45,93%,55%),0 2px 10px rgba(0,0,0,0.45);border:2px solid white;`
    : `box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid white;`;
  const size = selected ? 38 : 32;
  return L.divIcon({
    className: selected ? "member-marker member-marker--selected" : "member-marker",
    html: `<div style="background:hsl(152,45%,28%);color:white;border-radius:50%;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;font-size:14px;${ring}">👤</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const memberIcon = buildMemberIcon(false);
const memberIconSelected = buildMemberIcon(true);

function createClusterIcon(cluster: L.MarkerCluster) {
  const count = cluster.getChildCount();
  let size = "small";
  if (count >= 100) size = "large";
  else if (count >= 10) size = "medium";

  const sizes: Record<string, number> = { small: 36, medium: 44, large: 52 };
  const dim = sizes[size];

  // Highlight cluster if it contains the selected marker
  const containsSelected = cluster.getAllChildMarkers().some((m) => (m as any)._selected);
  const ring = containsSelected
    ? `box-shadow:0 0 0 4px hsl(45,93%,55%),0 2px 10px rgba(0,0,0,0.45);border:3px solid white;`
    : `box-shadow:0 2px 8px rgba(0,0,0,0.3);border:3px solid white;`;

  return L.divIcon({
    html: `<div style="background:hsl(152,45%,28%);color:white;border-radius:50%;width:${dim}px;height:${dim}px;display:flex;align-items:center;justify-content:center;font-size:${size === "large" ? 16 : 13}px;font-weight:700;${ring}">${count}</div>`,
    className: "member-cluster-icon",
    iconSize: L.point(dim, dim),
    iconAnchor: L.point(dim / 2, dim / 2),
  });
}

export default function MarkerClusterGroup({ markers, onMarkerClick, selectedId }: MarkerClusterGroupProps) {
  const map = useMap();
  const clusterRef = useRef<L.MarkerClusterGroup | null>(null);
  const markerMapRef = useRef<Map<string, L.Marker>>(new Map());

  // Rebuild cluster group when the marker set changes
  useEffect(() => {
    const group = L.markerClusterGroup({
      iconCreateFunction: createClusterIcon,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
    });

    clusterRef.current = group;
    const markerMap = new Map<string, L.Marker>();

    markers.forEach((m) => {
      const isSelected = m.id === selectedId;
      const marker = L.marker(m.coords, {
        icon: isSelected ? memberIconSelected : memberIcon,
        zIndexOffset: isSelected ? 1000 : 0,
      });
      (marker as any)._memberId = m.id;
      (marker as any)._selected = isSelected;
      marker.on("click", () => onMarkerClick(m.id));
      markerMap.set(m.id, marker);
      group.addLayer(marker);
    });

    markerMapRef.current = markerMap;
    map.addLayer(group);

    return () => {
      map.removeLayer(group);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, markers, onMarkerClick]);

  // Update icons when selection changes without rebuilding the whole cluster
  useEffect(() => {
    const group = clusterRef.current;
    if (!group) return;
    markerMapRef.current.forEach((marker, id) => {
      const isSelected = id === selectedId;
      const wasSelected = (marker as any)._selected;
      if (isSelected !== wasSelected) {
        (marker as any)._selected = isSelected;
        marker.setIcon(isSelected ? memberIconSelected : memberIcon);
        marker.setZIndexOffset(isSelected ? 1000 : 0);
      }
    });
    // Force cluster icons to re-render so the parent cluster reflects selection
    group.refreshClusters();
  }, [selectedId]);

  return null;
}
